import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  ShoppingBag,
  Truck,
  Tag,
  Info,
  AlertTriangle,
  Package,
  ArrowLeft,
  ArrowRight,
  Inbox,
  FileText,
  CreditCard,
  EyeOff,
  User,
  Mail,
  X,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { t } from "../../i18n";
import api from "../../api/client";
import logger from "../../utils/logger";
import LoadingScreen from "../ui/Loading/Loading";
import Breadcrumb from "../ui/Breadcrumb/Breadcrumb";
import Pagination from "../ui/Pagination/Pagination";
import "./Notifications.css";

function useTranslation(lang) {
  return useCallback(
    (key, arFallback, enFallback) => {
      const translated = t(lang, key);
      if (translated && translated !== key) return translated;
      return lang === "ar" ? arFallback : enFallback;
    },
    [lang]
  );
}

function formatDate(dateString, lang) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (lang === "ar") {
      return date.toLocaleDateString("ar-SA", {
        year: "numeric", month: "long", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

function timeAgo(dateString, lang) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (lang === "ar") {
      if (diffDays > 30) return formatDate(dateString, "ar");
      if (diffDays > 0) {
        if (diffDays === 1) return "منذ يوم";
        if (diffDays === 2) return "منذ يومين";
        if (diffDays >= 3 && diffDays <= 10) return `منذ ${diffDays} أيام`;
        return `منذ ${diffDays} يومًا`;
      }
      if (diffHours > 0) {
        if (diffHours === 1) return "منذ ساعة";
        if (diffHours === 2) return "منذ ساعتين";
        if (diffHours >= 3 && diffHours <= 10) return `منذ ${diffHours} ساعات`;
        return `منذ ${diffHours} ساعة`;
      }
      if (diffMins > 0) {
        if (diffMins === 1) return "منذ دقيقة";
        if (diffMins === 2) return "منذ دقيقتين";
        if (diffMins >= 3 && diffMins <= 10) return `منذ ${diffMins} دقائق`;
        return `منذ ${diffMins} دقيقة`;
      }
      return "منذ قليل";
    } else {
      if (diffDays > 30) return formatDate(dateString, "en");
      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
      return "Just now";
    }
  } catch {
    return dateString;
  }
}

function translateNotification(notification, lang) {
  const needsDownloadLink = ["digital", "download_ready"].includes(notification.type);
  const hasLink = notification.link || notification.link_url;

  if (lang !== "ar") {
    if (needsDownloadLink && !hasLink) {
      return { ...notification, link: "/my-downloads" };
    }
    return notification;
  }

  const title = notification.title || "";
  const message = notification.message || "";

  let tTitle = title
    .replace(/New order #(.*)/, "طلب جديد #$1")
    .replace(/Payment received for order #(.*)/, "تم استلام الدفع للطلب #$1")
    .replace(/Invoice (.*) is ready/, "الفاتورة $1 جاهزة")
    .replace(/Shipping update for order #(.*)/, "تحديث الشحن للطلب #$1")
    .replace(/Order #(.*) shipped/, "تم شحن الطلب #$1")
    .replace(/Order #(.*) delivered/, "تم تسليم الطلب #$1")
    .replace(/Order #(.*) is completed/, "تم إكمال الطلب #$1")
    .replace(/Order #(.*) completed/, "تم إكمال الطلب #$1")
    .replace(/Your digital items are ready/, t(lang, "notif_digital_ready", "عناصرك الرقمية جاهزة"))
    .replace(/Your download is ready/, t(lang, "notif_download_ready", "تحميلك جاهز"))
    .replace(/Welcome to Mousa Group/, t(lang, "notif_welcome", "مرحباً بك في مجموعة موسى"))
    .replace(/Your profile has been updated/, t(lang, "notif_profile_update", "تم تحديث ملفك الشخصي"))
    .replace(/Please verify your email address/, t(lang, "notif_email_verify", "يرجى التحقق من بريدك الإلكتروني"))
    .replace(/Your email has been verified/, t(lang, "notif_email_verified", "تم التحقق من بريدك الإلكتروني"))
    .replace(/Password reset link sent/, t(lang, "notif_password_reset", "تم إرسال رابط إعادة تعيين كلمة المرور"))
    .replace(/System maintenance scheduled/, t(lang, "notif_maintenance", "صيانة النظام مجدولة"))
    .replace(/System update available/, t(lang, "notif_system_update", "تحديث النظام متاح"))
    .replace(/New login detected/, t(lang, "notif_new_login", "تم اكتشاف تسجيل دخول جديد"))
    .replace(/Your password has been changed/, t(lang, "notif_password_changed", "تم تغيير كلمة المرور الخاصة بك"))
    .replace(/Service approved/, "تمت الموافقة على الخدمة")
    .replace(/Service rejected/, "تم رفض الخدمة")
    .replace(/Consultation approved/, "تمت الموافقة على الاستشارة")
    .replace(/Consultation rejected/, "تم رفض الاستشارة")
    .replace(/Course approved/, "تمت الموافقة على الكورس")
    .replace(/Course rejected/, "تم رفض الكورس")
    .replace(/Job approved/, "تمت الموافقة على الوظيفة")
    .replace(/Job rejected/, "تم رفض الوظيفة")
    .replace(/Seller product approved/, "تمت الموافقة على المنتج")
    .replace(/Seller product rejected/, "تم رفض المنتج")
    .replace(/Provider application approved/, "تمت الموافقة على طلب مقدم الخدمة")
    .replace(/Provider application rejected/, "تم رفض طلب مقدم الخدمة");

  let tMessage = message
    .replace(/Your order #(.*) has been created successfully/, "تم إنشاء طلبك #$1 بنجاح")
    .replace(/Your order #(.*) is completed\. Download links are now available/, "تم إكمال طلبك #$1. روابط التحميل متاحة الآن")
    .replace(/Your order #(.*) is completed/, "تم إكمال طلبك #$1")
    .replace(/Your order #(.*) has been completed/, "تم إكمال طلبك #$1")
    .replace(/Order #(.*) is completed/, "تم إكمال الطلب #$1")
    .replace(/Download links are now available/, "روابط التحميل متاحة الآن")
    .replace(/Download link is now available/, "رابط التحميل متاح الآن")
    .replace(/Shipping:\s*(.*)/, "الشحن: $1")
    .replace(/Pickup from store/, "الاستلام من المتجر")
    .replace(/Home delivery/, "التوصيل للمنزل")
    .replace(/ETA:\s*(.*)/, "الوقت المتوقع: $1")
    .replace(/Same Day/, "نفس اليوم")
    .replace(/Tracking code:\s*(.*)/, "رمز التتبع: $1")
    .replace(/Your payment of \$(.*) via (.*) was completed successfully/, "تم إتمام دفعتك بقيمة $$1 عبر $2 بنجاح")
    .replace(/Payment of \$(.*) received/, "تم استلام دفعة بقيمة $$1")
    .replace(/Payment received:\s*\$(.*)/, "تم استلام دفعة بقيمة $$1")
    .replace(/Invoice (.*) is now available for download/, "الفاتورة $1 متاحة للتحميل الآن")
    .replace(/You can download your invoice from your account/, "يمكنك تحميل فاتورتك من حسابك")
    .replace(/Your invoice (.*) has been generated/, "تم إنشاء فاتورتك $1")
    .replace(/was approved and is now visible to users\./, "تمت الموافقة عليه وهو ظاهر الآن للمستخدمين.")
    .replace(/was rejected by the admin team\./, "تم رفضه من فريق الإدارة.")
    .replace(/is pending admin review\./, "بانتظار مراجعة الإدارة.")
    .replace(/Your (.*) provider application was approved\./, "تمت الموافقة على طلب مقدم الخدمة الخاص بك: $1.")
    .replace(/Your (.*) provider application was rejected\./, "تم رفض طلب مقدم الخدمة الخاص بك: $1.")
    .replace(/Your product \"(.*)\" has been approved and is now eligible to appear in the store\./, "تمت الموافقة على المنتج \"$1\" وأصبح مؤهلاً للظهور في المتجر.")
    .replace(/Your product \"(.*)\" was rejected by the admin\./, "تم رفض المنتج \"$1\" من الإدارة.");

  const systemMsgPatterns = [
    { en: /You can now download your files from your account/, ar: t(lang, "notif_download_account", "يمكنك الآن تحميل ملفاتك من حسابك") },
    { en: /Click here to access your downloads/, ar: t(lang, "notif_download_link_text", "اضغط هنا للوصول إلى التحميلات") },
    { en: /Your digital items are ready for download/, ar: "عناصرك الرقمية جاهزة للتحميل" },
    { en: /Your files are now available in your downloads section/, ar: "ملفاتك متاحة الآن في قسم التحميلات" },
    { en: /Your download is ready\. Click to access/, ar: "تحميلك جاهز. اضغط للوصول" },
    { en: /Welcome to Mousa Group\. We're glad to have you/, ar: "مرحباً بك في مجموعة موسى. نحن سعداء بانضمامك" },
    { en: /Your profile information has been updated successfully/, ar: "تم تحديث معلومات ملفك الشخصي بنجاح" },
    { en: /Please verify your email by clicking the link sent to/, ar: "يرجى التحقق من بريدك الإلكتروني بالضغط على الرابط المرسل إلى" },
    { en: /Your email has been successfully verified/, ar: "تم التحقق من بريدك الإلكتروني بنجاح" },
    { en: /A password reset link has been sent to your email/, ar: "تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني" },
    { en: /Scheduled maintenance will occur on/, ar: "صيانة مجدولة ستتم في" },
    { en: /A new system update is available/, ar: "تحديث جديد للنظام متاح" },
    { en: /We detected a new login to your account from/, ar: "اكتشفنا تسجيل دخول جديد إلى حسابك من" },
    { en: /Your password was successfully changed/, ar: "تم تغيير كلمة المرور الخاصة بك بنجاح" },
    { en: /If this wasn't you, please contact support immediately/, ar: "إذا لم تكن أنت، يرجى الاتصال بالدعم فوراً" },
  ];

  systemMsgPatterns.forEach(({ en, ar }) => {
    tMessage = tMessage.replace(en, ar);
  });

  const translated = { ...notification, title: tTitle, message: tMessage };

  if (needsDownloadLink && !translated.link && !translated.link_url) {
    translated.link = "/my-downloads"; // ← مطابق لـ App.js
  }

  return translated;
}

const NOTIFICATION_CONFIG = {
  order: { icon: ShoppingBag, label: { ar: "طلب", en: "Order" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  order_status: { icon: ShoppingBag, label: { ar: "حالة الطلب", en: "Order Status" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  order_status_update: { icon: ShoppingBag, label: { ar: "تحديث الطلب", en: "Order Update" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  order_created: { icon: ShoppingBag, label: { ar: "طلب جديد", en: "New Order" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  order_placed: { icon: ShoppingBag, label: { ar: "طلب مُسجّل", en: "Order Placed" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  checkout: { icon: ShoppingBag, label: { ar: "دفع", en: "Checkout" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  checkout_success: { icon: ShoppingBag, label: { ar: "تم الدفع", en: "Checkout Success" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  order_completed: { icon: ShoppingBag, label: { ar: "طلب مكتمل", en: "Order Completed" }, colors: { light: { bg: "#FFF7ED", icon: "#f59e0b", dot: "#f59e0b" }, dark: { bg: "#2A1810", icon: "#fbbf24", dot: "#f59e0b" } } },
  shipment: { icon: Truck, label: { ar: "شحنة", en: "Shipping" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  shipping: { icon: Truck, label: { ar: "شحنة", en: "Shipping" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  shipping_update: { icon: Truck, label: { ar: "تحديث الشحن", en: "Shipping Update" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  shipping_status: { icon: Truck, label: { ar: "حالة الشحن", en: "Shipping Status" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  shipping_status_update: { icon: Truck, label: { ar: "تحديث الشحن", en: "Shipping Update" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  payment: { icon: CreditCard, label: { ar: "دفع", en: "Payment" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  payment_success: { icon: CreditCard, label: { ar: "دفع ناجح", en: "Payment" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  payment_status: { icon: CreditCard, label: { ar: "حالة الدفع", en: "Payment Status" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  payment_status_update: { icon: CreditCard, label: { ar: "تحديث الدفع", en: "Payment Update" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  payment_failed: { icon: AlertTriangle, label: { ar: "فشل الدفع", en: "Payment Failed" }, colors: { light: { bg: "#FEF2F2", icon: "#dc2626", dot: "#dc2626" }, dark: { bg: "#450A0A", icon: "#f87171", dot: "#dc2626" } } },
  payment_error: { icon: AlertTriangle, label: { ar: "خطأ دفع", en: "Payment Error" }, colors: { light: { bg: "#FEF2F2", icon: "#dc2626", dot: "#dc2626" }, dark: { bg: "#450A0A", icon: "#f87171", dot: "#dc2626" } } },
  invoice: { icon: FileText, label: { ar: "فاتورة", en: "Invoice" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  invoice_created: { icon: FileText, label: { ar: "فاتورة جديدة", en: "Invoice" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  invoice_paid: { icon: FileText, label: { ar: "فاتورة مدفوعة", en: "Invoice Paid" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  promo: { icon: Tag, label: { ar: "عرض", en: "Offer" }, colors: { light: { bg: "#FFFBEB", icon: "#b87d0a", dot: "#b87d0a" }, dark: { bg: "#451A03", icon: "#fbbf24", dot: "#b87d0a" } } },
  offer: { icon: Tag, label: { ar: "عرض", en: "Offer" }, colors: { light: { bg: "#FFFBEB", icon: "#b87d0a", dot: "#b87d0a" }, dark: { bg: "#451A03", icon: "#fbbf24", dot: "#b87d0a" } } },
  discount: { icon: Tag, label: { ar: "خصم", en: "Discount" }, colors: { light: { bg: "#FFFBEB", icon: "#b87d0a", dot: "#b87d0a" }, dark: { bg: "#451A03", icon: "#fbbf24", dot: "#b87d0a" } } },
  system: { icon: Info, label: { ar: "النظام", en: "System" }, colors: { light: { bg: "#F5F3FF", icon: "#7c3aed", dot: "#7c3aed" }, dark: { bg: "#1E1B4B", icon: "#a78bfa", dot: "#7c3aed" } } },
  info: { icon: Info, label: { ar: "معلومة", en: "Info" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  warning: { icon: AlertTriangle, label: { ar: "تنبيه", en: "Alert" }, colors: { light: { bg: "#FEF2F2", icon: "#dc2626", dot: "#dc2626" }, dark: { bg: "#450A0A", icon: "#f87171", dot: "#dc2626" } } },
  maintenance: { icon: Info, label: { ar: "صيانة", en: "Maintenance" }, colors: { light: { bg: "#F5F3FF", icon: "#7c3aed", dot: "#7c3aed" }, dark: { bg: "#1E1B4B", icon: "#a78bfa", dot: "#7c3aed" } } },
  update: { icon: Info, label: { ar: "تحديث", en: "Update" }, colors: { light: { bg: "#F5F3FF", icon: "#7c3aed", dot: "#7c3aed" }, dark: { bg: "#1E1B4B", icon: "#a78bfa", dot: "#7c3aed" } } },
  digital: { icon: Package, label: { ar: "رقمي", en: "Digital" }, colors: { light: { bg: "#F5F3FF", icon: "#7c3aed", dot: "#7c3aed" }, dark: { bg: "#1E1B4B", icon: "#a78bfa", dot: "#7c3aed" } } },
  download_ready: { icon: Package, label: { ar: "جاهز للتحميل", en: "Download Ready" }, colors: { light: { bg: "#F5F3FF", icon: "#7c3aed", dot: "#7c3aed" }, dark: { bg: "#1E1B4B", icon: "#a78bfa", dot: "#7c3aed" } } },
  user: { icon: User, label: { ar: "مستخدم", en: "User" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  welcome: { icon: User, label: { ar: "ترحيب", en: "Welcome" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  profile_update: { icon: User, label: { ar: "تحديث الملف", en: "Profile" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  email: { icon: Mail, label: { ar: "بريد", en: "Email" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  verification: { icon: Mail, label: { ar: "تحقق", en: "Verification" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  reset_password: { icon: Mail, label: { ar: "إعادة تعيين", en: "Reset Password" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  service_message: { icon: Mail, label: { ar: "رسالة", en: "Message" }, colors: { light: { bg: "#F0FDF4", icon: "#16a34a", dot: "#16a34a" }, dark: { bg: "#052E16", icon: "#4ade80", dot: "#16a34a" } } },
  freelancer_service_review: { icon: CheckCheck, label: { ar: "مراجعة خدمة", en: "Service Review" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  consultation_review: { icon: CheckCheck, label: { ar: "مراجعة استشارة", en: "Consultation Review" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  course_review: { icon: CheckCheck, label: { ar: "مراجعة كورس", en: "Course Review" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  job_review: { icon: CheckCheck, label: { ar: "مراجعة وظيفة", en: "Job Review" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  seller_product_review: { icon: CheckCheck, label: { ar: "مراجعة منتج", en: "Product Review" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  provider_profile_review: { icon: CheckCheck, label: { ar: "مراجعة طلب", en: "Application Review" }, colors: { light: { bg: "#EFF6FF", icon: "#2563eb", dot: "#2563eb" }, dark: { bg: "#102045", icon: "#60a5fa", dot: "#2563eb" } } },
  default: { icon: Bell, label: { ar: "إشعار", en: "Notification" }, colors: { light: { bg: "#FFFBEB", icon: "#b87d0a", dot: "#b87d0a" }, dark: { bg: "#451A03", icon: "#fbbf24", dot: "#b87d0a" } } },
};

const FILTER_TABS = [
  { key: "all",      labelAr: "الكل",        labelEn: "All" },
  { key: "messages", labelAr: "الرسائل",      labelEn: "Messages" },
  { key: "order",    labelAr: "طلباتي",       labelEn: "Orders" },
  { key: "shipment", labelAr: "الشحنات",      labelEn: "Shipping" },
  { key: "payment",  labelAr: "المدفوعات",    labelEn: "Payments" },
  { key: "promo",    labelAr: "العروض",       labelEn: "Offers" },
  { key: "system",   labelAr: "النظام",       labelEn: "System" },
];

const ITEMS_PER_PAGE = 8;
const SWIPE_THRESHOLD = 100;
const DISMISSED_KEY = "mg_dismissed_notifications";

function getDismissedIds() {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]")); }
  catch { return new Set(); }
}

function saveDismissedIdLocally(id, allServerIds = []) {
  try {
    const existing = getDismissedIds();
    existing.add(String(id));
    const serverSet = new Set(allServerIds.map(String));
    const pruned = [...existing].filter((sid) => serverSet.has(sid));
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(pruned));
  } catch {}
}

function NotificationItem({
  notification,
  index,
  isRTL,
  lang,
  isMobile,
  onMarkRead,
  onDismiss,
  onNavigate,
  tx,
}) {
  const config = NOTIFICATION_CONFIG[notification.type] || NOTIFICATION_CONFIG.default;
  const IconComponent = config.icon;
  const isUnread = !notification.is_read;
  const itemRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isDragging = useRef(false);
  const hasSwiped = useRef(false);
  const [isDismissing, setIsDismissing] = useState(false);

  const handleTouchStart = (e) => {
    hasSwiped.current = false;
    startX.current = e.touches[0].clientX;
    currentX.current = startX.current;
    isDragging.current = true;
    if (itemRef.current) itemRef.current.style.transition = "none";
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    currentX.current = e.touches[0].clientX;
    const diff = currentX.current - startX.current;
    if (itemRef.current) {
      itemRef.current.style.transform = `translateX(${diff}px)`;
      const opacity = Math.max(0.3, 1 - Math.abs(diff) / (SWIPE_THRESHOLD * 2.5));
      itemRef.current.style.opacity = String(opacity);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = currentX.current - startX.current;

    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      hasSwiped.current = true;
      const direction = diff > 0 ? 1 : -1;
      const dismissX = direction * (window.innerWidth * 0.6);
      if (itemRef.current) {
        itemRef.current.style.transition =
          "transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease";
        itemRef.current.style.transform = `translateX(${dismissX}px)`;
        itemRef.current.style.opacity = "0";
      }
      setIsDismissing(true);
      setTimeout(() => onDismiss(notification.id), 350);
    } else {
      if (itemRef.current) {
        itemRef.current.style.transition =
          "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease";
        itemRef.current.style.transform = "translateX(0)";
        itemRef.current.style.opacity = "1";
      }
    }
  };

  const handleDismissClick = (e) => {
    e.stopPropagation();
    setIsDismissing(true);
    setTimeout(() => onDismiss(notification.id), 400);
  };

  const handleItemClick = () => {
    if (hasSwiped.current) return;
    if (isMobile) {
      if (isUnread) onMarkRead(notification.id);
      onNavigate(notification.link_url || notification.link);
    } else {
      if (isUnread) onMarkRead(notification.id);
    }
  };

  const translated = translateNotification(notification, lang);
  const linkTarget = translated.link_url || translated.link;

  return (
    <article
      ref={itemRef}
      className={`notification-item ${isUnread ? "unread" : "read"} ${
        isDismissing ? "dismissing" : ""
      }`}
      style={{ animationDelay: `${index * 60}ms` }}
      onClick={handleItemClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="notification-swipe-bg">
        <EyeOff size={28} strokeWidth={2} />
      </div>

      <div
        className="notification-icon-wrapper"
        style={{ background: config.colors.light.bg }}
        data-theme-aware="true"
      >
        <IconComponent
          size={20}
          strokeWidth={2}
          style={{ color: config.colors.light.icon }}
          data-icon-color="true"
        />
      </div>

      <div className="notification-content">
        <div className="notification-meta-row">
          <span
            className="notification-type-badge"
            style={{
              background: config.colors.light.bg,
              color: config.colors.light.icon,
            }}
            data-theme-aware="true"
          >
            {isRTL ? config.label.ar : config.label.en}
          </span>
          <span className="notification-time">
            {timeAgo(notification.created_at, lang)}
          </span>
        </div>

        <h3 className="notification-title">{translated.title}</h3>

        <p className="notification-message">{translated.message}</p>

        {linkTarget && (
          <Link
            to={linkTarget}
            className="notification-link"
            onClick={(e) => e.stopPropagation()}
          >
            <span>{tx("view_details")}</span>
            {isRTL ? (
              <ArrowLeft size={14} strokeWidth={2.5} />
            ) : (
              <ArrowRight size={14} strokeWidth={2.5} />
            )}
          </Link>
        )}
      </div>

      <button
        className="notification-dismiss-btn"
        onClick={handleDismissClick}
        title={tx("dismiss")}
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </article>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [dismissingAll, setDismissingAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [activeFilter, setActiveFilter] = useState("all");
  const { lang } = useLanguage();
  const isRTL = lang === "ar";
  const tx = useTranslation(lang);

  const allNotificationIds = useRef([]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const data = await api.apiGet(`notifications?lang=${encodeURIComponent(lang)}&_t=${Date.now()}`);
        if (data?.status === "success") {
          const all = data.notifications || [];
          // Mirrors the navbar diagnostics so we can confirm the full account
          // notifications page receives valid UUID-backed rows from the API.
          logger.debug("Notifications page fetched notifications", {
            total: Array.isArray(all) ? all.length : 0,
            sampleIds: Array.isArray(all) ? all.slice(0, 5).map((item) => item?.id || null) : [],
            sampleTypes: Array.isArray(all) ? all.slice(0, 5).map((item) => item?.type || "") : [],
          });
          allNotificationIds.current = all.map((n) => n?.id).filter(Boolean);
          try {
            const existing = getDismissedIds();
            const serverSet = new Set(allNotificationIds.current.map(String));
            const pruned = [...existing].filter((sid) => serverSet.has(sid));
            localStorage.setItem(DISMISSED_KEY, JSON.stringify(pruned));
          } catch {}
          const dismissed = getDismissedIds();
          const visible = all.filter(
            (n) => n?.id && !n.is_dismissed && !dismissed.has(String(n.id))
          );
          // This makes it obvious when notifications exist on the server but are
          // being hidden locally by dismiss-state filtering in the account UI.
          logger.debug("Notifications page visible notifications after local filters", {
            visible: visible.length,
            dismissedLocalCount: dismissed.size,
          });
          setNotifications(visible);
        } else {
          setNotifications([]);
        }
      } catch (e) {
        logger.error("Error loading notifications", e);
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [lang]);

  useEffect(() => {
    const handleDismissed = (e) => {
      const { id } = e.detail;
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    };
    const handleDismissedAll = () => setNotifications([]);
    const handleMarkedRead = (e) => {
      const { id } = e.detail;
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    };
    const handleMarkedAllRead = () => {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    };

    window.addEventListener("mg:notification-dismissed", handleDismissed);
    window.addEventListener("mg:notifications-dismissed-all", handleDismissedAll);
    window.addEventListener("mg:notification-marked-read", handleMarkedRead);
    window.addEventListener("mg:notifications-marked-all-read", handleMarkedAllRead);
    return () => {
      window.removeEventListener("mg:notification-dismissed", handleDismissed);
      window.removeEventListener("mg:notifications-dismissed-all", handleDismissedAll);
      window.removeEventListener("mg:notification-marked-read", handleMarkedRead);
      window.removeEventListener("mg:notifications-marked-all-read", handleMarkedAllRead);
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setVisibleCount(ITEMS_PER_PAGE);
  }, [notifications.length, activeFilter]);

  const handleMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      await api.apiPost("notifications/mark-all-read", {});
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      window.dispatchEvent(new CustomEvent("mg:notifications-marked-all-read"));
    } catch (e) {
      logger.error("Error marking all as read", e);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDismissAll = async () => {
    try {
      setDismissingAll(true);
      setNotifications([]);
      window.dispatchEvent(new CustomEvent("mg:notifications-dismissed-all"));
      await api.apiPost("notifications/dismiss-all", {});
    } catch (e) {
      logger.error("Dismiss all API failed", e);
      allNotificationIds.current.forEach((id) =>
        saveDismissedIdLocally(id, allNotificationIds.current)
      );
    } finally {
      setDismissingAll(false);
    }
  };

  const handleMarkRead = useCallback(async (id) => {
    try {
      await api.apiPost(`notifications/${id}/mark-read`, {});
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      window.dispatchEvent(new CustomEvent("mg:notification-marked-read", { detail: { id } }));
    } catch (e) {
      logger.error("Error marking notification as read", e);
    }
  }, []);

  const handleDismiss = useCallback(async (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    window.dispatchEvent(new CustomEvent("mg:notification-dismissed", { detail: { id } }));
    try {
      await api.apiPost(`notifications/${id}/dismiss`, {});
    } catch (e) {
      logger.error("Dismiss API failed, falling back to localStorage", e);
      saveDismissedIdLocally(id, allNotificationIds.current);
    }
  }, []);

  const handleNavigate = useCallback((link) => {
    if (link) {
      if (String(link).startsWith("http")) {
        window.open(link, "_blank");
      } else {
        navigate(link);
      }
    } else {
      navigate("/account/notifications");
    }
  }, [navigate]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  }, []);

  const MESSAGE_TYPES = new Set(["service_message"]);
  const ORDER_TYPES = new Set([
    "order", "order_status", "order_status_update", "order_created", "order_placed",
    "checkout", "checkout_success", "order_completed"
  ]);
  const SHIPMENT_TYPES = new Set([
    "shipment", "shipping", "shipping_update", "shipping_status", "shipping_status_update"
  ]);
  const PAYMENT_TYPES = new Set([
    "payment", "payment_status", "payment_status_update", "payment_success",
    "payment_failed", "payment_error", "invoice", "invoice_created", "invoice_paid"
  ]);
  const PROMO_TYPES = new Set(["promo", "offer", "discount"]);
  const SYSTEM_TYPES = new Set([
    "system", "maintenance", "update", "info", "warning",
    "digital", "download_ready",
    "user", "welcome", "profile_update",
    "email", "verification", "reset_password",
    "freelancer_service_review", "consultation_review", "course_review",
    "job_review", "seller_product_review", "provider_profile_review"
  ]);

  const TAB_TYPE_MAP = {
    messages: MESSAGE_TYPES,
    order: ORDER_TYPES,
    shipment: SHIPMENT_TYPES,
    payment: PAYMENT_TYPES,
    promo: PROMO_TYPES,
    system: SYSTEM_TYPES,
  };

  const filteredNotifications = useMemo(() => {
    if (activeFilter === "all") return notifications;
    const typeSet = TAB_TYPE_MAP[activeFilter];
    if (!typeSet) return notifications.filter((n) => n.type === activeFilter);
    return notifications.filter((n) => typeSet.has(n.type));
  }, [notifications, activeFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE)),
    [filteredNotifications]
  );

  const displayedNotifications = useMemo(() => {
    if (isMobile) {
      return filteredNotifications.slice(0, visibleCount);
    }
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredNotifications.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredNotifications, isMobile, visibleCount, currentPage]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const tabCounts = useMemo(() => {
    const counts = { all: notifications.length };
    Object.entries(TAB_TYPE_MAP).forEach(([tab, typeSet]) => {
      counts[tab] = notifications.filter((n) => typeSet.has(n.type)).length;
    });
    return counts;
  }, [notifications]);

  const breadcrumbItems = useMemo(() => {
    return [{ label: tx("notifications") }];
  }, [tx]);

  const headerActions = (
    <>
      {unreadCount > 0 && (
        <button
          className="notifications-mark-all-btn"
          onClick={handleMarkAllRead}
          disabled={markingAll}
        >
          <CheckCheck size={16} strokeWidth={2} />
          <span className="btn-text">{tx("mark_all_read")}</span>
        </button>
      )}
      {notifications.length > 0 && (
        <button
          className="notifications-dismiss-all-btn"
          onClick={handleDismissAll}
          disabled={dismissingAll}
          title={tx("dismiss_all")}
        >
          <span className="btn-text">
            {dismissingAll ? tx("dismissing") : tx("dismiss_all")}
          </span>
        </button>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="notifications-page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="notifications-container">
          <LoadingScreen lang={lang} />
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page" dir={isRTL ? "rtl" : "ltr"}>
      <Breadcrumb items={breadcrumbItems} />

      <header className="notifications-header">
        <div className="notifications-header-content">
          <div className="notifications-title-wrapper">
            <div className="notifications-header-icon">
              <Bell size={24} strokeWidth={2} />
            </div>
            <h1 className="notifications-header-title">
              {tx("notifications")}
            </h1>
          </div>
    
        </div>
      </header>

      <div className="notifications-container">
        <div className="notifications-subheader-bar">
          <p className="notifications-subheader-text">
            {tx("notifications_subheader")}
          </p>
        </div>

        <div className="notifications-actions-row">
          {headerActions}
        </div>

        <div className="notifications-inner">
          <div className="notifications-filter-tabs" role="tablist">
            {FILTER_TABS.map(({ key, labelAr, labelEn }) => {
              const count = tabCounts[key] || 0;
              const isActive = activeFilter === key;
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={isActive}
                  className={`notifications-filter-tab ${isActive ? "active" : ""}`}
                  onClick={() => setActiveFilter(key)}
                >
                  <span>{isRTL ? labelAr : labelEn}</span>
                  {count > 0 && (
                    <span className="notifications-filter-tab-count">{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {filteredNotifications.length === 0 ? (
            <div className="notifications-empty">
              <div className="notifications-empty-icon">
                <Inbox size={56} strokeWidth={1} />
              </div>
              <h2>
                {activeFilter === "all"
                  ? tx("no_notifications")
                  : tx("no_notifications_filter")}
              </h2>
              <p>
                {activeFilter === "all"
                  ? tx("no_notifications_desc")
                  : tx("try_all_tab")}
              </p>
            </div>
          ) : (
            <>
              <section className="notifications-list-section">
                <div className="notifications-list">
                  {displayedNotifications.map((notification, index) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      index={index}
                      isRTL={isRTL}
                      lang={lang}
                      isMobile={isMobile}
                      onMarkRead={handleMarkRead}
                      onDismiss={handleDismiss}
                      onNavigate={handleNavigate}
                      tx={tx}
                    />
                  ))}
                </div>
              </section>

              {!isMobile && totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}

              {isMobile && filteredNotifications.length > displayedNotifications.length && (
                <div className="load-more-wrapper">
                  <button className="load-more-btn" onClick={handleLoadMore}>
                    {tx("load_more")}
                    <span className="load-more-count">
                      ({displayedNotifications.length} / {filteredNotifications.length})
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
